import React, { useEffect, useState } from 'react';
import LoaderAnnonce from '../../../components/LoaderAnnonce';
import GestionClient from './GestionClient';
import ReferralWithdrawals from './ReferralWithdrawals';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import { AppDispatch, RootState } from '../../../store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCopy, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { fetchUserDetails } from '../../../userThunk';
import { Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import Team from './Team';
import '../../../css/GestionParrainage.css';
import SendMailSponsor from './SendMailSponsor';
import { Alert, Badge, Card, Space } from 'antd';
import { getEntrepriseTeamByParentId } from '../../../services/entrepriseTeamService';
import { UserModel } from '../../../models/UserModel';
import { getAllClients, getAllReferredClients } from '../../../services/User_service';
import { ExclamationCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { getAllRefundRequests, getUserRefundRequests } from '../../../services/RefundRequest_service';
import { trackMenuClick } from "../../../services/menuClick_service";

function usePagination<T>(data: T[], itemsPerPage: number = 20) {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    const paginatedData = data.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToPage = (page: number) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    return { paginatedData, currentPage, totalPages, goToNextPage, goToPrevPage, goToPage, setCurrentPage };
}

const GestionParrainage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('gestion-clients');
    const [loading, setLoading] = useState<boolean>(false);
    const authUser = useSelector((state: RootState) => state.user.user);
    const [secretary, setSecretary] = useState<any>(null);
    const dispatch: AppDispatch = useDispatch();
    const [clients, setClients] = useState<UserModel[]>([]);
    const [totalPendingAmount, setTotalPendingAmount] = useState<number>(0);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    const role = localStorage.getItem("role");
    const parentIdString = localStorage.getItem("userId");

    const parentId = (parentIdString && role !== "ADMIN") ? parseInt(parentIdString) : undefined;

    const isEntrepriseString = localStorage.getItem('isEntreprise');
    const isEntreprise = isEntrepriseString ? JSON.parse(isEntrepriseString) : false;

    const userStatus = localStorage.getItem("userStatus") || "anonymous";

    const [showSendModal, setShowSendModal] = useState<boolean>(false);

    const isAdmin = role === "ADMIN";
    const userId = localStorage.getItem("userId") || '';

    // Détection de la taille d'écran
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 576);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fonction pour calculer le total des montants en attente
    const fetchPendingAmount = async () => {
        try {
            const response = isAdmin ? await getAllRefundRequests() : await getUserRefundRequests(userId);
            if (response.status === 200) {
                const data = response.data;
                const totalPending = data.reduce((acc: number, r: any) => {
                    return acc + (r.status === 'pending' ? Number(r.amount || 0) : 0);
                }, 0);
                setTotalPendingAmount(totalPending);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des montants en attente", error);
        }
    };

    const handleRefundRequestsUpdate = (totalPending: number) => {
        setTotalPendingAmount(totalPending);
    };

    const getListUsers = async () => {
        setLoading(true);
        try {
            let clients: UserModel[] = [];

            if (role === "ADMIN") {
                clients = await getAllReferredClients();
            } else {
                clients = await getAllClients();
                if (parentId) {
                    clients = clients.filter(client => client.parent_id === parentId);
                }
            }

            clients = clients.filter(client =>
                client.parent_id !== null &&
                client.role !== 'SECRETAIRE'
            );

            setClients(clients);
        } catch (error) {
            console.error("Erreur lors de la récupération des clients", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSecretary = async () => {
        try {
            const entrepriseId = localStorage.getItem("userId");
            if (entrepriseId) {
                const data = await getEntrepriseTeamByParentId(parseInt(entrepriseId));
                if (data) {
                    setSecretary(data);
                }
            }
        } catch (error) {
            console.error("Erreur lors de la récupération d'assistants", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSecretary();
        getListUsers();
        fetchPendingAmount();
    }, []);

    useEffect(() => {
        if (!authUser) {
            dispatch(fetchUserDetails());
        }
    }, [dispatch, authUser]);

    const updateTab = (active: string) => {
        setLoading(true);
        setActiveTab(active);
        setTimeout(() => setLoading(false), 500);
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'gestion-clients':
                return <GestionClient
                    parentId={role === "ADMIN" ? undefined : parentId}
                    isAdmin={role === "ADMIN"}
                    getListUsers={getListUsers}
                    clients={clients}
                    setClients={setClients}
                />;
            case 'retraits':
                return <ReferralWithdrawals
                    authUser={authUser}
                    secretary={secretary}
                    setSecretary={setSecretary}
                    fetchSecretary={fetchSecretary}
                    onRefundRequestsUpdate={handleRefundRequestsUpdate}
                />;
            case 'secretary':
                return <Team
                    authUser={authUser}
                    secretary={secretary}
                    setSecretary={setSecretary}
                    fetchSecretary={fetchSecretary}
                />;
            default:
                return null;
        }
    };

    const referralLink = authUser?.code_parrainage
        ? `${window.location.origin}/register?ref=${authUser.code_parrainage}`
        : null;

    const copyToClipboard = (text: string) => {
        trackMenuClick('parrainage-copier-code', 'Copier le code de parrainage');
        navigator.clipboard.writeText(text)
            .then(() => {
                toast.success('Le lien de parrainage a été copié dans le presse-papier');
            })
            .catch(() => {
                toast.error('Erreur lors de la copie du lien');
            });
    };

    const openSendModal = () => {
        trackMenuClick('parrainage-partage-email', 'Partager par e-mail');
        setShowSendModal(true);
    };

    const getUserFullName = () => {
        if (!authUser) return '';

        if (authUser.is_entreprise || isEntreprise) {
            return authUser.raison_social || authUser.name || '';
        } else {
            const firstName = authUser.firstname || '';
            const lastName = authUser.name || '';
            return `${firstName} ${lastName}`.trim();
        }
    };

    // Tooltip avec meilleur positionnement sur mobile
    const renderTooltip = (props: any) => (
        <Tooltip id="tooltip-info" {...props}>
            Partagez ce lien avec d'autres personnes pour qu'elles s'inscrivent et que vous puissiez gagner des commissions sur leurs achats.
        </Tooltip>
    );

    if (userStatus === "anonymous") {
        return (
            <>
                <ToastContainer />
                <div className="gp-root container-fluid">
                    <div className="mt-5 text-center">
                        <Alert
                            className="custom-alert-white"
                            message="Module de parrainage indisponible"
                            description="Ravi de vous compter parmi nous ! Publiez votre première annonce et finalisez votre inscription pour profiter de ce module."
                            type="warning"
                            showIcon
                            icon={<ExclamationCircleFilled style={{ color: '#eb8635' }} />}
                            style={{
                                fontSize: isMobile ? '0.95rem' : '1.1rem',
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <ToastContainer />
            <div className="gp-root container-fluid">
                {(authUser && referralLink) && role !== "ADMIN" ? (
                    <div className="gp-referral-section mb-3">
                        <h6 className="gp-referral-title">
                            Lien de parrainage :
                            {!isMobile && (
                                <span
                                    className="gp-referral-link"
                                    onClick={() => copyToClipboard(referralLink)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {referralLink}
                                </span>
                            )}
                            <FontAwesomeIcon
                                icon={faCopy}
                                className="gp-icon gp-icon-copy"
                                onClick={() => copyToClipboard(referralLink)}
                                role="button"
                                title="Copier le lien"
                            />
                            <OverlayTrigger
                                placement={isMobile ? "bottom" : "top"}
                                overlay={renderTooltip}
                            >
                                <span className="gp-icon-wrapper" role="button">
                                    <FontAwesomeIcon icon={faInfoCircle} className="gp-icon gp-icon-info" />
                                </span>
                            </OverlayTrigger>
                        </h6>
                        
                        {isMobile && (
                            <div className="mb-2" style={{ fontSize: '0.85rem', color: '#666' }}>
                                <span
                                    onClick={() => copyToClipboard(referralLink)}
                                    role="button"
                                    style={{ 
                                        color: 'var(--gp-primary)', 
                                        textDecoration: 'underline',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {referralLink}
                                </span>
                            </div>
                        )}

                        <div className="gp-referral-row">
                            <h6 className="gp-referral-title gp-code-label">Votre code de parrainage :</h6>

                            <button
                                type="button"
                                className="gp-code-box"
                                onClick={() => copyToClipboard(authUser?.code_parrainage)}
                                aria-label="Copier le code de parrainage"
                            >
                                <span className="gp-code-text">{authUser?.code_parrainage}</span>
                                <FontAwesomeIcon icon={faCopy} className="gp-icon gp-code-copy" />
                            </button>

                            <Button
                                variant="outline-primary"
                                onClick={openSendModal}
                                className="gp-btn-share"
                            >
                                Partager par e-mail
                            </Button>
                        </div>

                        {/* Badges Parent/Assistant avec meilleure responsivité */}
                        <div className="gp-badges-container mt-3">
                            <Space 
                                direction={isMobile ? "vertical" : "horizontal"} 
                                size="middle" 
                                style={{ width: isMobile ? '100%' : 'auto' }}
                            >
                                <Badge.Ribbon text="Parent" color="#F4A261">
                                    <Card 
                                        size="small" 
                                        style={{ 
                                            width: isMobile ? '100%' : 160, 
                                            fontSize: '1.01rem', 
                                            fontWeight: 'bold', 
                                            textAlign: 'left' 
                                        }}
                                    >
                                        {(Number(authUser?.referralPercentage ?? 0) - Number(secretary?.sharedPercentageReferral ?? 0)).toFixed(2)} %
                                    </Card>
                                </Badge.Ribbon>
                                <Badge.Ribbon text="Assistant" color="#5bc1d1">
                                    <Card 
                                        size="small" 
                                        style={{ 
                                            width: isMobile ? '100%' : 160, 
                                            fontSize: '1.01rem', 
                                            fontWeight: 'bold', 
                                            textAlign: 'left' 
                                        }}
                                    >
                                        {(secretary?.sharedPercentageReferral ?? 0).toFixed(2)} %
                                    </Card>
                                </Badge.Ribbon>
                            </Space>
                        </div>

                        {authUser?.usedReferralCode && (
                            <h6 className="gp-used-code">Code de parrainage utilisé à l'inscription :
                                <span className="gp-used-code-box">
                                    <span className="gp-used-code-text">{authUser.usedReferralCode}</span>
                                </span>
                            </h6>
                        )}
                    </div>
                ) : null}

                {role !== "ADMIN" ? (
                    <div className="gp-stats-inline mb-4">
                        <div className="gp-card mt-2">
                            <div className="gp-card-label">Parrainages</div>
                            <div className="gp-card-value">{authUser?.referralCount ?? 0}</div>
                        </div>

                        <div className="gp-card mt-2">
                            <div className="gp-card-label">Ma solde {isMobile && <br />}disponible</div>
                            <div className="gp-card-value">
                                {new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    .format(Number(authUser?.solde ?? 0))} €
                            </div>
                        </div>

                        <div className="gp-card mt-2">
                            <div className="gp-card-label">Montants {isMobile && <br />}demandés</div>
                            <div className="gp-card-value gp-pending">
                                {totalPendingAmount.toFixed(2)} €
                            </div>
                        </div>

                        <div className="gp-card mt-2">
                            <div className="gp-card-label">Commission {isMobile && <br />}non payée</div>
                            <div className="gp-card-value gp-commission">
                                {(((authUser?.commissionUnpaid ?? 0) / 100)).toFixed(2)} €
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mb-4">
                        <h4>Gestion du parrainage</h4>
                        <p className="text-muted">Visualisez tous les clients parrainés et leurs informations.</p>
                    </div>
                )}

                <div className="border-top mt-3"></div>

                {
                    (role === "ADMIN" || (role !== "ADMIN" && clients.length !== 0))
                        ?
                        <>
                            <div>
                                <ul className="nav nav-underline gp-nav d-flex flex-wrap">
                                    <li className="nav-item">
                                        <a
                                            href="#"
                                            className={`nav-link ${activeTab === 'gestion-clients' ? 'active' : ''}`}
                                            onClick={e => { e.preventDefault(); updateTab('gestion-clients'); }}
                                        >
                                            {role === "ADMIN" ? (isMobile ? "Clients" : "Tous les clients parrainés") : "Clients"}
                                        </a>
                                    </li>
                                    {(isEntreprise || authUser?.user_type === "professionnel") && role !== "ADMIN" ? (
                                        <li className="nav-item">
                                            <a
                                                href="#"
                                                className={`nav-link ${activeTab === 'secretary' ? 'active' : ''}`}
                                                onClick={e => { e.preventDefault(); updateTab('secretary'); }}
                                            >
                                                Assistant
                                            </a>
                                        </li>
                                    ) : null}
                                    <li className="nav-item">
                                        <a
                                            href="#"
                                            className={`nav-link ${activeTab === 'retraits' ? 'active' : ''}`}
                                            onClick={e => { e.preventDefault(); updateTab('retraits'); }}
                                        >
                                            Retraits
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <div className="mt-4">
                                {loading ? <LoaderAnnonce /> : renderContent()}
                            </div>
                        </>
                        :
                        <div className='mt-5 text-center'>
                            <Alert
                                className='custom-alert-white'
                                description="Partagez votre code ou lien de parrainage avec d'autres personnes pour qu'elles s'inscrivent et que vous puissiez gagner des commissions sur leurs achats."
                                type="warning"
                                showIcon
                                style={{
                                    fontSize: isMobile ? '0.95rem' : '1.1rem',
                                }}
                                icon={<InfoCircleOutlined style={{ color: '#eb8635', fontSize: isMobile ? '18px' : '20px' }} />}
                            />
                        </div>
                }

                {role !== "ADMIN" && (
                    <SendMailSponsor
                        show={showSendModal}
                        onHide={() => setShowSendModal(false)}
                    />
                )}
            </div>
        </>
    );
}

export default GestionParrainage;

const PaginatedTable = ({ component: Component, parentId }: { component: React.ComponentType<any>, parentId?: number }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const { paginatedData, currentPage, totalPages, goToNextPage, goToPrevPage } = usePagination(data, 5);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setLoading(false);
        };
        fetchData();
    }, []);

    return (
        <div>
            {loading ? <LoaderAnnonce /> : <Component parentId={parentId} data={paginatedData} />}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                    <button onClick={goToPrevPage} disabled={currentPage === 1}><FontAwesomeIcon icon={faArrowLeft} /></button>
                    <span>{currentPage} / {totalPages}</span>
                    <button onClick={goToNextPage} disabled={currentPage === totalPages}><FontAwesomeIcon icon={faArrowRight} /></button>
                </div>
            )}
        </div>
    );
};